/**
 * تعديل/حذف طلب من قبل مدير النظام — لأي طلب لم يُستلم بعد، حتى بعد موعد الإقفال.
 * بعد الإقفال يطلب الخادم سبباً مكتوباً يُحفظ في سجل التدقيق (BR-11).
 * الطلب المستلَم لا يُعدَّل ولا يُحذف (BR-10) — يُعاد فتح استلامه أولاً من صفحة الاستلام.
 */
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useActiveItems, groupByCategory } from "../../hooks/useItems";
import { formatDate } from "../../lib/date";
import { ITEM_UNIT_LABELS } from "../../types";
import type { MealEntry } from "../../types";

const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="card w-full max-w-2xl my-8 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{title}</h2>
          <button type="button" className="text-sand-500 hover:text-gray-900" onClick={onClose} aria-label="إغلاق">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditModal({ entry, onClose }: { entry: MealEntry; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: items = [] } = useActiveItems();
  const groups = groupByCategory(items);

  const [qty, setQty] = useState<Record<number, string>>(
    () => Object.fromEntries(entry.lines.map((l) => [l.itemId, String(l.qty)]))
  );
  const [notes, setNotes]   = useState(entry.notes ?? "");
  const [reason, setReason] = useState("");
  const [error, setError]   = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError("");
    const lines = items
      .map((it) => ({ itemId: it.id, qty: parseFloat(qty[it.id] ?? "") }))
      .filter((l) => Number.isFinite(l.qty) && l.qty > 0);
    if (lines.length === 0) { setError("لا يمكن ترك الطلب فارغاً — احذفه بدلاً من ذلك"); return; }

    setSaving(true);
    try {
      await api.put(`/meal-entries/${entry.id}`, {
        lines, notes: notes.trim() || null, reason: reason.trim() || null,
      });
      await qc.invalidateQueries({ queryKey: ["meal-entries"] });
      await qc.invalidateQueries({ queryKey: ["report"] });
      onClose();
    } catch (err) {
      setError(errMsg(err, "تعذّر حفظ التعديل"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`تعديل طلب ${entry.directorate.nameAr} — ${formatDate(entry.entryDate)}`} onClose={onClose}>
      {groups.map((g) => (
        <fieldset key={g.id} className="border border-sand-300 rounded-lg p-3">
          <legend className="px-2 text-xs font-semibold text-sand-600">{g.nameAr}</legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {g.items.map((it) => (
              <div key={it.id}>
                <label className="label" htmlFor={`adm-${it.id}`}>
                  {it.nameAr} <span className="text-sand-400 text-xs">({ITEM_UNIT_LABELS[it.unit]})</span>
                </label>
                <input id={`adm-${it.id}`} type="number" min="0" inputMode="decimal" dir="ltr" className="input"
                  step={it.maxDecimalPlaces > 0 ? String(1 / 10 ** it.maxDecimalPlaces) : "1"}
                  placeholder="0" value={qty[it.id] ?? ""} disabled={saving}
                  onChange={(e) => setQty((q) => ({ ...q, [it.id]: e.target.value }))} />
              </div>
            ))}
          </div>
        </fieldset>
      ))}
      <div>
        <label className="label" htmlFor="adm-notes">ملاحظات الطلب</label>
        <textarea id="adm-notes" rows={2} className="input resize-none" value={notes} disabled={saving}
          onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div>
        <label className="label" htmlFor="adm-reason">سبب التعديل <span className="text-xs text-sand-500">(إلزامي بعد موعد الإقفال — يُحفظ في سجل التدقيق)</span></label>
        <input id="adm-reason" className="input" value={reason} disabled={saving}
          placeholder="مثال: تصحيح عدد الغداء بعد مراجعة الفرع" onChange={(e) => setReason(e.target.value)} />
      </div>
      {error && <div className="alert-error">{error}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>إلغاء</button>
        <button type="button" className="btn-primary" onClick={() => void save()} disabled={saving}>
          {saving ? "جاري الحفظ..." : "حفظ التعديل"}
        </button>
      </div>
    </Modal>
  );
}

function DeleteModal({ entry, onClose }: { entry: MealEntry; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError]   = useState("");
  const [saving, setSaving] = useState(false);

  const remove = async () => {
    setError(""); setSaving(true);
    try {
      await api.delete(`/meal-entries/${entry.id}`, { data: { reason: reason.trim() || null } });
      await qc.invalidateQueries({ queryKey: ["meal-entries"] });
      await qc.invalidateQueries({ queryKey: ["report"] });
      onClose();
    } catch (err) {
      setError(errMsg(err, "تعذّر حذف الطلب"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="حذف طلب" onClose={onClose}>
      <p className="text-sm">
        سيُحذف طلب <strong>{entry.directorate.nameAr}</strong> — {entry.governorate.nameAr} ليوم{" "}
        <strong>{formatDate(entry.entryDate)}</strong>. تبقى نسخة كاملة منه في سجل التدقيق.
      </p>
      <div>
        <label className="label" htmlFor="del-reason">سبب الحذف <span className="text-xs text-sand-500">(إلزامي بعد موعد الإقفال)</span></label>
        <input id="del-reason" className="input" value={reason} disabled={saving}
          onChange={(e) => setReason(e.target.value)} placeholder="مثال: طلب مكرر بالخطأ" />
      </div>
      {error && <div className="alert-error">{error}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>إلغاء</button>
        <button type="button" className="btn-danger" onClick={() => void remove()} disabled={saving}>
          {saving ? "جاري الحذف..." : "حذف نهائي"}
        </button>
      </div>
    </Modal>
  );
}

export default function AdminEntryActions({ entry }: { entry: MealEntry }) {
  const [mode, setMode] = useState<"edit" | "delete" | null>(null);
  if (entry.receivedAt) {
    return <span className="text-[11px] text-sand-400" title="أعد فتح الاستلام أولاً من صفحة الاستلام">مستلَم — مقفل</span>;
  }
  return (
    <>
      <div className="flex gap-1">
        <button type="button" className="px-2 py-0.5 text-xs rounded border border-primary-300 text-primary-700 hover:bg-primary-50"
          onClick={() => setMode("edit")}>تعديل</button>
        <button type="button" className="px-2 py-0.5 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => setMode("delete")}>حذف</button>
      </div>
      {mode === "edit"   && <EditModal   entry={entry} onClose={() => setMode(null)} />}
      {mode === "delete" && <DeleteModal entry={entry} onClose={() => setMode(null)} />}
    </>
  );
}
